import { CellContext, ColumnDef } from '@shape-ds/components';
import { BuildColumns, SeverityPills, buildColumnsObject } from '@shape/lighthouse-components';
import Status from 'components/Status/Status';
import { t } from 'i18next';
import { caseEvaluationMap } from 'pages/Asset/tabs/Cases/components/Filters/SeverityPanel';
import { actionStatusSeverityMap, CaseListItem } from 'queries/cases/types';

import { sortDatesDesc } from 'utils';

const COLUMN_CONFIGURATIONS = {
  EXPANDED_COLUMNS: ['asset.id', 'asset.name', 'category', 'description'],
  COLUMN_SIZES: {
    SEVERITY: 60,
    START_DATE: 79,
    NAME: 111,
    DIMENSION: 140,
    STATUS: 100,
    ACTION_STATUS: 100,
    UPDATED_AT: 120,
  },
};

export type ListItemColumnDefinition = {
  [key in keyof CaseListItem]: Partial<ColumnDef<CaseListItem>>;
};

const memoizedTranslation = (() => {
  const cache = new Map<string, string>();

  return (key: string, fallback?: string) => {
    if (cache.has(key)) {
      const cachedValue = cache.get(key);
      if (cachedValue) return cachedValue;
    }

    const translated = t(key, { defaultValue: fallback });
    cache.set(key, translated);
    return translated;
  };
})();

export const createListItemColumns = (): BuildColumns<CaseListItem>['columns'] => ({
  severity: {
    enableSorting: false,
    size: COLUMN_CONFIGURATIONS.COLUMN_SIZES.SEVERITY,
    cell: ({ row }: CellContext<CaseListItem, unknown>) => (
      <div className="column-severity">
        <SeverityPills severity={caseEvaluationMap[row.original.severity]} totalPills={4} />
      </div>
    ),
  },
  start_date: {
    size: COLUMN_CONFIGURATIONS.COLUMN_SIZES.START_DATE,
    sortingFn: (a: { original: CaseListItem }, b: { original: CaseListItem }) =>
      sortDatesDesc(a.original.start_date, b.original.start_date),
  },
  name: {
    enableSorting: false,
    size: COLUMN_CONFIGURATIONS.COLUMN_SIZES.NAME,
  },
  status: {
    size: COLUMN_CONFIGURATIONS.COLUMN_SIZES.STATUS,
    cell: ({ row }: CellContext<CaseListItem, unknown>) => {
      const stage = row.original.status;
      return <span style={{ fontWeight: 500 }}> {stage}</span>;
    },
  },
  action_status: {
    size: COLUMN_CONFIGURATIONS.COLUMN_SIZES.ACTION_STATUS,
    cell: ({ row }: CellContext<CaseListItem, unknown>) => {
      const actionStatus = row.original.action_status as keyof typeof actionStatusSeverityMap;
      return (
        <Status status={actionStatusSeverityMap[actionStatus]} variant="text">
          {memoizedTranslation(`case:actions.status.${actionStatus}`)}
        </Status>
      );
    },
  },
  updated_at: {
    size: COLUMN_CONFIGURATIONS.COLUMN_SIZES.UPDATED_AT,
  },
});

export const useListColumns = (isExpanded: boolean): Array<ColumnDef<CaseListItem>> => {
  const defaultColumns = createListItemColumns();

  const filterColumns = !isExpanded
    ? Object.keys(defaultColumns).filter(
        (key) => !COLUMN_CONFIGURATIONS.EXPANDED_COLUMNS.includes(key),
      )
    : undefined;

  return buildColumnsObject<CaseListItem>({
    columns: defaultColumns,
    getLabel: (key) => memoizedTranslation(`case:tableColumns.${key}`),
    overrideColumns: filterColumns,
  });
};
